import React, { Component, Suspense } from 'react';
import { Switch, Redirect } from 'react-router-dom';
import { Breadcrumb, BreadcrumbItem, Container } from 'reactstrap';
import { connect } from 'react-redux';
import {
  CHeader,
  CFooter,
  CSidebar,
  CSidebarNav,
  CNavItem,
  CNavGroup,
  CContainer,
  CBreadcrumb,
  CBreadcrumbItem
} from '@coreui/react';
// sidebar nav config
import getWhiteLabelMenu from '../../data/getWhiteLabelMenu';
import * as actions from '../../actions';
import { setCollectionsOverviewPage } from '../../modules/actions';
// routes config
// eslint-disable-next-line
import getRoutes from '../../whiteLabelRoutes';
import DefaultFooter from './DefaultFooter';
import DefaultHeader from './DefaultHeader';
import { DiscountAlert } from '../../elements/DiscountKey';
import MaintenanceAlert from '../../elements/Alerts/MaintenanceAlert';
import NewDesignBanner from '../../components/NewDesign/NewDesignBanner';
import history from '../../helpers/history';
import UpgradeAccountModal from '../../elements/User/Account/UpgradeAccountModal';
import UpgradeSlideshowAccountModal from '../../elements/User/Account/UpgradeSlideshowAccountModal';
import { ShopTaxModal } from '../../components/ShopTaxSetup/ShopTaxModal';
// import EasterSaleModal from '../../elements/User/Message/EasterSaleModal';
// import SeasonStartSaleModal from '../../elements/User/Message/SeasonStartSaleModal';
// import NphotoPriceIncreaseModal from '../../elements/User/Message/NphotoPriceIncreaseModal';
import TrialEndsAlert from '../../elements/TrialEndsAlert';
import SubscriptionRoute from '../../components/Route/SubscriptionRoute';
import FeaturesDowngradeAlert from '../../elements/Subscription/FeaturesDowngradeAlert';
import FeaturesDowngradeModal from '../../elements/Subscription/FeaturesDowngradeModal';
import { DEAL_DISCOUNT_KEYS } from '../../modules/constants/subscriptions';
import UpdateAddonsModal from '../../pages/subscription/UpdateAddonsModal';
import PaymentMethodSetupRequiredModal from '../../pages/subscription/PaymentMethodSetupRequiredModal';
import { getTranslationKey } from '../../modules/utils';
import StripeMigrationAlert from '../../elements/Subscription/StripeMigrationAlert';
import StripeMigrationNote from '../../elements/Subscription/StripeMigrationNote';

interface DefaultLayoutProps {
  auth: any;
  environment: any;
  accessRules: any;
  setCollectionsOverviewPage: (page: number) => void;
}

interface DefaultLayoutState {
  locationIsCollectionEdit: boolean;
  locationIsPortfolioEdit: boolean;
  isDiscountAlertOpen: boolean;
  routes: any[];
  sidebarUnfoldable: boolean;
  sidebarVisible: boolean;
}

interface NavItem {
  name: string;
  url?: string;
  icon?: string;
  badge?: { variant: string; text: string };
  children?: NavItem[];
  attributes?: Record<string, any>;
}

const renderNavItems = (items: NavItem[]): React.ReactNode =>
  items.map((item, index) => {
    // If item has children, render as a nav group (dropdown)
    if (item.children && item.children.length > 0) {
      return (
        <CNavGroup
          key={index}
          data-cy={`nav-group-${item.name}`}
          toggler={
            <>
              {item.icon && <i className={`nav-icon ${item.icon}`} />}
              <span className="nav-link-text">{item.name}</span>
            </>
          }
        >
          {item.children.map((child, childIndex) => (
            <CNavItem key={childIndex} href={`#${child.url}`} data-cy={`nav-item-${child.name}`}>
              {child.icon && <i className={`nav-icon ${child.icon}`} />}
              <span className="nav-link-text">{child.name}</span>
            </CNavItem>
          ))}
        </CNavGroup>
      );
    }

    // Regular nav item
    return (
      <CNavItem
        key={index}
        href={`#${item.url}`}
        {...(item.attributes || {})}
        data-cy={`nav-item-${item.name}`}
      >
        {item.icon && <i className={`nav-icon ${item.icon}`} />}
        <span className="nav-link-text">{item.name}</span>
        {item.badge && (
          <span className={`badge badge-${item.badge.variant} ms-auto`}>{item.badge.text}</span>
        )}
      </CNavItem>
    );
  });

declare global {
  interface Window {
    LANGUAGE_SW: any;
  }
}

class DefaultLayout extends Component<DefaultLayoutProps, DefaultLayoutState> {
  constructor(props: DefaultLayoutProps) {
    super(props);

    let isDiscountAlertOpen = false;

    if (!!props.auth?.digistore?.discountKey?._id) {
      isDiscountAlertOpen = !DEAL_DISCOUNT_KEYS.includes(props.auth?.digistore?.discountKey?._id);
    }

    this.state = {
      locationIsCollectionEdit: history.location.pathname.startsWith('/collection/edit/'),
      locationIsPortfolioEdit: history.location.pathname.startsWith('/portfolios/edit/'),
      isDiscountAlertOpen,
      routes: [],
      sidebarUnfoldable: window.innerWidth > 991,
      sidebarVisible: window.innerWidth > 991
    };

    history
      .listen((location) => {
        this.setState({
          locationIsCollectionEdit: location.pathname.startsWith('/collection/edit/'),
          locationIsPortfolioEdit: location.pathname.startsWith('/portfolios/edit/')
        });
      })
      .bind(this);
  }

  componentDidMount() {
    this.setState(() => ({ routes: getRoutes() }));
    window.addEventListener('resize', this.handleResize);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  handleResize = () => {
    const isDesktop = window.innerWidth > 991;
    this.setState({ sidebarUnfoldable: isDesktop, sidebarVisible: isDesktop });
  };

  getNavigation() {
    const { auth } = this.props;
    const navigation = getWhiteLabelMenu(auth) as { items?: any[] };

    if (auth.isAdmin && navigation.items) {
      return {
        ...navigation,
        items: [
          {
            name: getTranslationKey('adminDashboard.title'),
            url: '/admin/dashboard',
            icon: 'icon-diamond'
          },
          ...navigation.items
        ]
      };
    }

    return navigation;
  }

  toggleSidebar = () => {
    this.setState((prevState) => ({ sidebarVisible: !prevState.sidebarVisible }));
  };

  handleNavClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    const isMobile = window.innerWidth <= 991;
    // Close sidebar on mobile when a nav link (not a group toggle) is clicked
    if (isMobile && target.closest('.nav-link') && !target.closest('.nav-group-toggle')) {
      this.setState({ sidebarVisible: false });
    }

    // The galleries menu entry must always land on the first page of the overview.
    // Only the page is reset — an active search term stays applied.
    if (target.closest('a')?.getAttribute('href') === '#/collections') {
      this.props.setCollectionsOverviewPage(0);
    }
  };

  closeDiscountAlert = () => {
    this.setState({ isDiscountAlertOpen: false });
  };

  render() {
    const { auth } = this.props;
    const {
      locationIsCollectionEdit,
      locationIsPortfolioEdit,
      isDiscountAlertOpen,
      routes,
      sidebarUnfoldable,
      sidebarVisible
    } = this.state;

    const isOnEditPages = locationIsCollectionEdit || locationIsPortfolioEdit;

    const isOnTrial = auth?.subscription?.plan === 'free-trial';
    const isNotOnSubscriptionPage = !window.location.href.includes('subscription/pricing');
    const trialEndsAt = auth?.subscription?.freeTrialEndsAt;
    const userIsOnFreeTrial = isOnTrial && isNotOnSubscriptionPage && trialEndsAt;

    const navigation = this.getNavigation() as { items?: NavItem[] };

    return (
      <div className="app">
        <UpgradeAccountModal />
        <UpdateAddonsModal />
        <PaymentMethodSetupRequiredModal />
        <UpgradeSlideshowAccountModal />
        <FeaturesDowngradeModal />
        <CHeader position="fixed" className="app-header">
          <CContainer fluid className="px-0">
            <DefaultHeader toggleSidebar={this.toggleSidebar} />
          </CContainer>
        </CHeader>
        <div className="app-body">
          <CSidebar
            key={sidebarUnfoldable ? 'desktop' : 'mobile'}
            data-cy="appSidebar"
            position="fixed"
            unfoldable={sidebarUnfoldable}
            visible={sidebarVisible}
            onVisibleChange={() => {}}
            className={`app-sidebar${sidebarVisible ? '' : ' sidebar-off'}`}
          >
            <CSidebarNav data-cy="navigate" onClick={this.handleNavClick}>
              {navigation.items && renderNavItems(navigation.items)}
            </CSidebarNav>
          </CSidebar>
          <main className={`main${sidebarVisible ? '' : ' sidebar-hidden'}`}>
            <NewDesignBanner />
            <MaintenanceAlert />
            {!isOnEditPages && (
              <CBreadcrumb data-cy="appBreadcrumb" className="my-0">
                {routes
                  .filter((route) => window.location.hash.includes(route.path))
                  .map((route, index) => (
                    <CBreadcrumbItem
                      key={index}
                      href={`#${route.path}`}
                      active={`#${route.path}` === window.location.hash}
                    >
                      {route.name}
                    </CBreadcrumbItem>
                  ))}
              </CBreadcrumb>
            )}
            {locationIsPortfolioEdit && (
              <Breadcrumb>
                <BreadcrumbItem>
                  <a href="/#/">Home</a>
                </BreadcrumbItem>
                <BreadcrumbItem>
                  <a href="/#/portfolios">{getTranslationKey('portfolio.portfolios')}</a>
                </BreadcrumbItem>
                <BreadcrumbItem active>{getTranslationKey('portfolio.title')}</BreadcrumbItem>
              </Breadcrumb>
            )}
            {!isOnEditPages && (
              // @ts-ignore: DiscountAlert prop types mismatch across variants
              <DiscountAlert
                isOpen={isDiscountAlertOpen}
                closeDiscountAlert={this.closeDiscountAlert}
              />
            )}
            {!isOnEditPages && <FeaturesDowngradeAlert />}
            {!isOnEditPages && <StripeMigrationAlert />}
            {!isOnEditPages && <StripeMigrationNote />}
            {!isOnEditPages && userIsOnFreeTrial && (
              <TrialEndsAlert trialEndsAt={auth?.subscription?.freeTrialEndsAt} />
            )}
            <Container
              fluid
              style={{ height: 'calc(100% - 70px)', margin: '24px 0 40px 0', padding: '0 30px' }}
            >
              <Suspense
                fallback={
                  <div className="text-center">
                    <div className="loader" />
                  </div>
                }
              >
                <Switch>
                  {routes.map((route, idx) =>
                    route.component ? (
                      <SubscriptionRoute
                        key={idx}
                        path={route.path}
                        exact={route.exact}
                        pageId={route.id}
                        render={(props) => <route.component {...props} />}
                      />
                    ) : null
                  )}
                  <Redirect exact from="/" to="/dashboard" />
                  {!!routes.length && <Redirect from="*" to="/dashboard" />}
                </Switch>
              </Suspense>
            </Container>
          </main>
        </div>
        {!locationIsCollectionEdit && (
          <CFooter className="app-footer">
            <DefaultFooter />
          </CFooter>
        )}
        <ShopTaxModal />
      </div>
    );
  }
}

function mapStateToProps({
  auth,
  environment,
  accessRules
}: any): Omit<DefaultLayoutProps, 'setCollectionsOverviewPage'> {
  return { auth, environment, accessRules };
}

export default connect(mapStateToProps, { ...actions, setCollectionsOverviewPage })(DefaultLayout);
